import {
  MEMBERSHIP_DEMO_URL,
  MEMBERSHIP_PRICING_TIERS,
  MEMBERSHIP_SIGNUP_URL,
} from '../../constants/membershipPricingConstants';

/** Membership application intake (Phase 1). */
export { MEMBERSHIP_SIGNUP_URL, MEMBERSHIP_DEMO_URL };

export const HERO_HEADLINE = 'California Climate Action Portal';

export const HERO_SUBHEADLINE =
  'GHG inventories, indicators, policy analysis, engagement tools, and reporting for California local governments.';

export const HERO_TRUST_LINE = "Built by UC Berkeley's CoolClimate Network.";

export const WORKFLOW_SECTION = {
  title: 'Filling the Gaps to Advance Climate Action',
  subtitle: 'A complete workflow for local governments — from understanding through implementation.',
} as const;

export const WORKFLOW_COLUMNS = [
  {
    title: 'Understand',
    items: ['GHG inventories', 'Equity indicators', 'Peer benchmarking', 'Community profiles'],
  },
  {
    title: 'Prioritize',
    items: ['Scenario modeling', 'Policy evaluation', 'Peer comparisons', 'Climate action planning'],
  },
  {
    title: 'Implement',
    items: ['Reports', 'Community engagement', 'Progress tracking', 'Funding support'],
  },
] as const;

export const MEMBERSHIP_HERO = {
  titleLines: ['Spend Less on Climate Planning', 'Invest More in Climate Action'],
  tagline:
    'ClimatePlans is a living climate planning and implementation platform for California local governments.',
  paragraphs: [
    'Our mission is to transform climate planning from a resource-intensive, complicated, infrequent process into a data-driven cycle of active management and public engagement.',
    'ClimatePlans continuously updates GHG inventories and planning tools for all California jurisdictions so local governments and consultants spend less time on data and more time on policy, engagement, funding, and implementation.',
  ],
  goalCallout: {
    label: 'Our Goal',
    prefix: 'Help members achieve ',
    emphasis: 'at least $10 saved for every $1 invested in membership',
    suffix: ' by reducing repetitive planning work and redirecting staff toward implementation.',
  },
} as const;

export const NEEDS_ASSESSMENT_PDF_URL = '/docs/CoolClimate-Needs-Assessment-2025.pdf';
export const NEEDS_ASSESSMENT_SOURCE_LABEL = 'CoolClimate Network Needs Assessment, 2025';

export const MEMBERSHIP_COST_COMPARISON = {
  intro:
    'Traditional climate planning often requires tens of thousands of dollars for greenhouse gas inventories and hundreds of thousands for Climate Action Plans. Annual membership is typically only a small fraction of those costs while providing continuous access to data, reporting, benchmarking, funding intelligence, and implementation tools.',
  disclaimer:
    'These comparisons are not savings guarantees. They show that annual membership is generally a fraction of what responding jurisdictions reported spending on one GHG inventory or CAP. ClimatePlans supports recurring data, reporting, engagement, benchmarking, and implementation workflows; local tailoring remains essential.',
  columns: [
    'Population',
    'Avg. reported GHG inventory cost',
    'Affiliate membership',
    'Affiliate as % of one inventory',
    'Avg. reported CAP cost',
    'Regular membership',
    'Regular as % of one CAP',
  ],
  rows: MEMBERSHIP_PRICING_TIERS.rows,
} as const;

export type WorkflowStep = {
  icon: string;
  text: string;
};

export const WORKFLOW_COMPARISON = {
  title: 'Two Ways to Do the Work',
  traditional: {
    label: 'Traditional workflow',
    items: [
      { icon: '🔍', text: 'Search for funding opportunities' },
      { icon: '👥', text: 'Hire consultant' },
      { icon: '📁', text: 'Collect data' },
      { icon: '📊', text: 'Produce greenhouse gas inventory' },
      { icon: '📝', text: 'Draft Climate Action Plan' },
      { icon: '📄', text: 'Publish static PDF' },
      { icon: '🔁', text: 'Repeat every few years' },
    ] satisfies WorkflowStep[],
  },
  climatePlans: {
    label: 'ClimatePlans workflow',
    items: [
      { icon: '🗺️', text: 'Continuously updated statewide data' },
      { icon: '⚡', text: 'Generate inventory reports automatically' },
      { icon: '📈', text: 'Benchmark against California peers' },
      { icon: '💡', text: 'Identify funding opportunities' },
      { icon: '🤝', text: 'Engage households, businesses, and local governments' },
      { icon: '📋', text: 'Track implementation progress' },
      { icon: '🔄', text: 'Update planning continuously' },
    ] satisfies WorkflowStep[],
  },
} as const;

export const PUBLIC_ACCESS = {
  tierLabel: 'Public Access',
  heading: 'Explore California Climate Data — Free',
  description:
    'Explore California communities, compare jurisdictions, and understand local conditions before joining.',
  items: [
    'Explore every California community',
    'Compare jurisdictions',
    'View indicators and GHG inventories',
    'Discover funding opportunities',
    'Access public dashboards',
  ],
} as const;

export const AFFILIATE_MEMBERSHIP = {
  tierLabel: 'Affiliate Membership',
  heading: 'Inventory, Reporting & Benchmarking',
  priceLabel: 'Starting at $2,500/year',
  intro: 'Downloadable reports, statewide benchmarking, and engagement tools.',
  items: [
    'Downloadable GHG inventory reports',
    'Data and figure downloads',
    'ICLEI-compatible reporting',
    'Statewide peer benchmarking',
    'Member webinars and working groups',
    'Climate intelligence updates',
    'Household and business calculator license',
    'Local Government Workspace',
    'CoolClimate Network member recognition',
  ],
} as const;

export const REGULAR_MEMBERSHIP = {
  tierLabel: 'Regular Membership',
  heading: 'Planning & Implementation',
  priceLabel: 'Starting at $5,000/year',
  intro: 'Everything in Affiliate Membership, plus:',
  items: [
    'Climate Planning Workspace',
    'Living implementation tracking',
    'Funding opportunity matching',
    'Policy and measure library',
    'Custom dashboards',
    'Community engagement tools',
    'Organization management',
    'Staff training',
    'Technical support',
  ],
} as const;

export const WHY_AGENCIES_JOIN = {
  title: 'Why Agencies Join',
  items: [
    { text: 'Reduce recurring planning costs', icon: 'dollar' as const },
    { text: 'Complete inventories in days instead of months', icon: 'clock' as const },
    { text: 'Reuse data across reports', icon: 'database' as const },
    { text: 'Discover funding opportunities faster', icon: 'target' as const },
    { text: 'Benchmark against every California jurisdiction', icon: 'chart' as const },
    { text: 'Spend more time on implementation', icon: 'building' as const },
  ],
} as const;

export const FEATURED_TOOLS_SECTION = {
  title: 'CoolClimate Member Resources',
  subtitle: 'Analysis, reporting, engagement, and jurisdiction-specific tools.',
} as const;

export type FeaturedToolMembershipLevel = 'free' | 'affiliate' | 'regular';

export const FEATURED_TOOL_MEMBERSHIP_LABELS: Record<FeaturedToolMembershipLevel, string> = {
  free: 'Free Level',
  affiliate: 'Affiliate Level',
  regular: 'Regular Level',
};

export const FEATURED_MEMBER_TOOLS = [
  {
    id: 'climateplans',
    title: 'ClimatePlans Basic',
    description: [
      '100+ indicators for every California city and county',
      'Data, figures, and custom narrative',
    ],
    href: '/',
    membershipLevel: 'free',
    isCurrentApp: true,
  },
  {
    id: 'advanced-analysis',
    title: 'Analysis and Reports',
    description: [
      'Statewide analysis and benchmarking',
      'Downloadable GHG inventory and data reports',
    ],
    href: '/law-impact',
    membershipLevel: 'affiliate',
  },
  {
    id: 'local-government-workspace',
    title: 'Local Government Workspace',
    description: [
      'Customize data, charts, and narrative',
      'Add custom data and figures',
      'Local admin privileges',
    ],
    href: '#public-vs-membership',
    membershipLevel: 'affiliate',
  },
  {
    id: 'data-download',
    title: 'Data & Figure Download',
    description: 'Export charts, tables, and datasets for reports.',
    href: '#public-vs-membership',
    membershipLevel: 'affiliate',
  },
  {
    id: 'calculator',
    title: 'CoolClimate Household Calculator',
    description: 'Community engagement',
    href: process.env.REACT_APP_CALCULATOR_URL || 'https://coolclimate.org/calculator',
    membershipLevel: 'affiliate',
    external: true,
  },
  {
    id: 'business',
    title: 'CoolClimate Calculator for Businesses',
    description: 'Organizational emissions',
    href: process.env.REACT_APP_BUSINESS_URL || 'https://coolclimate.org/org-calc',
    membershipLevel: 'affiliate',
    external: true,
  },
  {
    id: 'custom-portal',
    title: 'Custom Climate Action Portal',
    description:
      'A customized portal tailored to your jurisdiction\'s goals and workflows.',
    href: '#public-vs-membership',
    membershipLevel: 'regular',
  },
  {
    id: 'consumption-based-ghg-inventory',
    title: 'Consumption-Based GHG Inventory',
    description: [
      'Tract-level CBEI',
      'Sector-level analysis',
      'Local, domestic, and international emissions',
      'Target setting',
    ],
    href: '#public-vs-membership',
    membershipLevel: 'regular',
  },
  {
    id: 'scenario-planning',
    title: 'Scenario Planning',
    description: [
      'Explore GHG mitigation scenarios',
      'Estimate impacts and costs of future actions',
    ],
    href: '#public-vs-membership',
    membershipLevel: 'regular',
  },
  {
    id: 'policy-explorer',
    title: 'Policy Explorer',
    description: [
      'Review ordinances, climate action plans, and general plans',
      'Compare policies from peer jurisdictions',
      'Draft with examples and templates',
    ],
    href: '/law-impact',
    membershipLevel: 'regular',
  },
  {
    id: 'challenge',
    title: 'Cool Campus Challenge',
    description: 'Campus engagement and behavior change',
    href: process.env.REACT_APP_CHALLENGE_URL || 'https://coolcampuschallenge.org/',
    membershipLevel: 'regular',
    external: true,
  },
  {
    id: 'advanced-local-government-admin',
    title: 'Advanced Local Government Admin',
    description: [
      'Household and resident action dashboard',
      'Create and manage custom Challenges',
      'Assign and manage local user content',
      'Messaging for local users',
    ],
    href: '#public-vs-membership',
    membershipLevel: 'regular',
  },
] as const;

export const SUCCESS_STORIES_SECTION = {
  title: 'Success Stories',
  subtitle: 'How California communities use CoolClimate tools.',
} as const;

export const SUCCESS_STORIES = [
  {
    id: 'san-francisco',
    title: 'City and County of San Francisco',
    role: 'Current implementation partner',
    excerpt:
      'Industry-leading consumption-based GHG inventory methodology and employee engagement programs.',
    isComingSoon: false,
  },
  {
    id: 'coolcalifornia',
    title: 'CoolCalifornia',
    role: '20+ year statewide partnership',
    excerpt:
      'Partnership with the California Air Resources Board on tools for residents, businesses, schools, and communities.',
    isComingSoon: false,
  },
  {
    id: 'more-stories',
    title: 'More member stories coming',
    role: '',
    excerpt: 'More member stories will be added as organizations join.',
    isComingSoon: true,
  },
] as const;

export const GHG_INVENTORY_SECTION = {
  title: 'The first comprehensive greenhouse gas inventory for California\u2019s cities and counties',
  items: [
    '459 cities and 58 counties — sector-based GHG inventories',
    'Only standardized methodology for statewide peer benchmarking',
    'CEQA- and ICLEI Community Protocol-compliant',
    'Built by UC Berkeley with state, local, and academic partners',
    'IOU and municipal utility energy data',
    'Consumption-based inventories to census block group',
    'Historical reconstruction back to 2005',
    'Updated annually with the latest data',
    'Supports CAPs, CEQA, grants, benchmarking, and reporting',
    'Relevant, Accurate, Consistent, Transparent, Comprehensive, and Cost-Effective',
  ],
} as const;

export const WHY_COOLCLIMATE_SECTION = {
  title: 'Why UC Berkeley\u2019s CoolClimate Network?',
  items: [
    '20+ years developing leading climate research and software for California',
    'Partner on CoolCalifornia.org',
    'Winner of a $1.3M California Legislature grant to develop ClimatePlans.org',
    'Technical advisory board with dozens of experts',
    'Developer of the CoolClimate Calculator with millions of users',
    '10+ years of behavior-based competitions for California residents, universities, and businesses',
  ],
} as const;

export const FINAL_CTA = {
  title: 'Join California\u2019s Climate Planning Network',
  text: 'Cities, counties, campuses, and organizations use CoolClimate tools for inventory, benchmarking, and implementation.',
  trustLine: 'Built by UC Berkeley to support California climate planning.',
} as const;
